import { useState, useEffect, useContext, useRef } from "react";
import { FaPlus, FaCheck, FaTimes, FaSync } from "react-icons/fa";
import { motion, AnimatePresence } from "framer-motion";
import AuthContext from "../../contexts/AuthContext";
import {
  createStockMovement,
  getInventoryItems,
  getOpeningStock,
} from "../../utils/inventoryApi";

export default function StockMovements() {
  const { restaurantId } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [openingStockItems, setOpeningStockItems] = useState([]);
  const toastTimeout = useRef(null);
  const [toast, setToast] = useState({ show: false, message: "", type: "" });
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Table state
  const [rows, setRows] = useState([]);
  const [editingRow, setEditingRow] = useState(null);

  // Transfer settings
  const [transferSettings, setTransferSettings] = useState({
    toLocation: "KITCHEN",
    reason: "KITCHEN_REQUEST",
    reference: "",
    notes: "",
  });

  const reasons = [
    { value: "KITCHEN_REQUEST", label: "Kitchen Request" },
    { value: "WASTE", label: "Waste" },
    { value: "EXPIRED", label: "Expired" },
    { value: "DAMAGED", label: "Damaged" },
    { value: "SPOILED", label: "Spoiled" },
  ];

  const locations = [
    { value: "KITCHEN", label: "Kitchen" },
    { value: "WASTE", label: "Waste" },
  ];

  useEffect(() => {
    fetchItems();
    fetchOpeningStock();
    // eslint-disable-next-line
  }, [restaurantId]);

  async function fetchItems() {
    setLoading(true);
    setError("");
    try {
      const data = await getInventoryItems(restaurantId);
      setItems(data || []);
    } catch (err) {
      setError("Failed to fetch inventory items");
      showToast("Failed to fetch inventory items", "error");
    }
    setLoading(false);
  }

  async function fetchOpeningStock() {
    try {
      const data = await getOpeningStock(restaurantId, "main");
      setOpeningStockItems(data || []);
    } catch (err) {
      console.error("Failed to fetch opening stock:", err);
    }
  }

  // Get all available items (from both inventory and opening stock)
  const getAllItems = () => {
    const inventoryItems = items.map((item) => ({
      id: item.id,
      prodId: item.prodId,
      prodName: item.name,
      uom: item.unit,
      stockQty: item.currentStock || 0,
      source: "inventory",
    }));

    const openingItems = openingStockItems.map((item) => ({
      id: item.id,
      prodId: item.prodId,
      prodName: item.prodName,
      uom: item.uom,
      stockQty: item.stockQty || 0,
      source: "opening",
    }));

    // Merge and remove duplicates based on prodId
    const merged = [...inventoryItems, ...openingItems];
    const unique = merged.filter(
      (item, index, self) =>
        index === self.findIndex((t) => t.prodId === item.prodId)
    );

    return unique;
  };

  function handleAddRow() {
    const newRow = {
      id: `new-${Date.now()}`,
      sno: rows.length + 1,
      prodId: "",
      prodName: "",
      uom: "",
      stockQty: "",
      transferQty: "",
      transferDate: new Date().toISOString().split("T")[0],
    };
    setRows([...rows, newRow]);
    setEditingRow(newRow.id);
  }

  function handleEditRow(rowId) {
    setEditingRow(rowId);
  }

  function handleCancelEdit() {
    setEditingRow(null);
  }

  function handleDeleteRow(rowId) {
    setRows(rows.filter((row) => row.id !== rowId));
    if (editingRow === rowId) {
      setEditingRow(null);
    }
  }

  function handleProductNameChange(rowId, prodName) {
    const allItems = getAllItems();
    const selectedItem = allItems.find((item) => item.prodName === prodName);

    setRows(
      rows.map((row) => {
        if (row.id === rowId) {
          return {
            ...row,
            prodName: prodName,
            prodId: selectedItem ? selectedItem.prodId : "",
            uom: selectedItem ? selectedItem.uom : "",
            stockQty: selectedItem ? selectedItem.stockQty : "",
          };
        }
        return row;
      })
    );
  }

  function handleInputChange(rowId, field, value) {
    setRows(
      rows.map((row) => {
        if (row.id === rowId) {
          return { ...row, [field]: value };
        }
        return row;
      })
    );
  }

  async function handleSaveAll() {
    setError("");
    setSuccess("");

    if (rows.length === 0) {
      setError("Please add at least one item to transfer");
      return;
    }

    // Validate all rows
    const invalidRows = rows.filter(
      (row) => !row.prodName || !row.transferQty || row.transferQty <= 0
    );

    if (invalidRows.length > 0) {
      setError("Please fill all required fields for all items");
      return;
    }

    // Additional validation: Check if transfer quantity exceeds available stock
    for (const row of rows) {
      const allItems = getAllItems();
      const selectedItem = allItems.find(
        (item) => item.prodName === row.prodName
      );

      if (selectedItem && Number(row.transferQty) > selectedItem.stockQty) {
        setError(
          `Transfer quantity (${row.transferQty}) exceeds available stock (${selectedItem.stockQty}) for ${row.prodName}`
        );
        return;
      }
    }

    try {
      // Create movements for all rows
      const movementPromises = rows.map(async (row) => {
        const allItems = getAllItems();
        const selectedItem = allItems.find(
          (item) => item.prodName === row.prodName
        );

        if (!selectedItem) {
          throw new Error(`Item ${row.prodName} not found`);
        }

        const movementData = {
          itemId: selectedItem.prodId, // Use prodId instead of id
          type: "TRANSFER",
          quantity: Number(row.transferQty),
          fromLocation: "MAIN_STORE",
          toLocation: transferSettings.toLocation,
          reason: transferSettings.reason,
          reference: transferSettings.reference,
          notes: transferSettings.notes,
          costPerUnit: 0, // Will be calculated from item data
        };

        console.log("Sending movement data:", movementData);

        return await createStockMovement(restaurantId, movementData);
      });

      await Promise.all(movementPromises);

      // Clear the form
      setRows([]);
      setTransferSettings({
        toLocation: "KITCHEN",
        reason: "KITCHEN_REQUEST",
        reference: "",
        notes: "",
      });

      setSuccess("All items transferred successfully!");
      showToast("All items transferred successfully!", "success");

      // Refresh data to get updated stock quantities
      await fetchItems();
      await fetchOpeningStock();
    } catch (err) {
      console.error("Error saving movements:", err);
      setError("Failed to transfer items. Please try again.");
      showToast("Failed to transfer items", "error");
    }
  }

  function showToast(message, type = "success") {
    setToast({ show: true, message, type });
    if (toastTimeout.current) clearTimeout(toastTimeout.current);
    toastTimeout.current = setTimeout(
      () => setToast({ show: false, message: "", type: "" }),
      3000
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-rose-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-800 dark:text-white">
            Stock Movements
          </h1>
          <p className="text-sm sm:text-base text-gray-600 dark:text-gray-300 mt-1 sm:mt-2">
            Transform items from main store to kitchen or waste
          </p>
        </div>
        <div className="flex flex-wrap gap-2 sm:gap-3">
          <button
            onClick={handleAddRow}
            className="bg-rose-500 hover:bg-rose-600 text-white px-3 sm:px-4 py-2 rounded-lg flex items-center gap-1 sm:gap-2 transition-colors text-sm"
          >
            <FaPlus />
            <span className="hidden sm:inline">Add Item</span>
            <span className="sm:hidden">Add</span>
          </button>
          <button
            onClick={() => {
              fetchItems();
              fetchOpeningStock();
            }}
            className="bg-amber-500 hover:bg-amber-600 text-white px-3 sm:px-4 py-2 rounded-lg flex items-center gap-1 sm:gap-2 transition-colors text-sm"
          >
            <FaSync />
            <span className="hidden sm:inline">Refresh</span>
            <span className="sm:hidden">Refresh</span>
          </button>
        </div>
      </div>

      {/* Transfer Settings */}
      <div className="bg-white dark:bg-slate-800 rounded-lg p-3 sm:p-4 lg:p-6 shadow-sm border border-gray-200 dark:border-slate-700">
        <h3 className="text-base sm:text-lg font-semibold text-gray-800 dark:text-white mb-3 sm:mb-4">
          Transfer Settings
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          <div>
            <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 sm:mb-2">
              To Location
            </label>
            <select
              value={transferSettings.toLocation}
              onChange={(e) =>
                setTransferSettings((prev) => ({
                  ...prev,
                  toLocation: e.target.value,
                }))
              }
              className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-rose-500 dark:bg-slate-700 dark:text-white text-sm"
            >
              {locations.map((location) => (
                <option key={location.value} value={location.value}>
                  {location.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 sm:mb-2">
              Reason
            </label>
            <select
              value={transferSettings.reason}
              onChange={(e) =>
                setTransferSettings((prev) => ({
                  ...prev,
                  reason: e.target.value,
                }))
              }
              className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-rose-500 dark:bg-slate-700 dark:text-white text-sm"
            >
              {reasons.map((reason) => (
                <option key={reason.value} value={reason.value}>
                  {reason.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 sm:mb-2">
              Reference
            </label>
            <input
              type="text"
              value={transferSettings.reference}
              onChange={(e) =>
                setTransferSettings((prev) => ({
                  ...prev,
                  reference: e.target.value,
                }))
              }
              placeholder="PO_001, REQ_001, etc."
              className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-rose-500 dark:bg-slate-700 dark:text-white text-sm"
            />
          </div>
          <div>
            <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 sm:mb-2">
              Notes
            </label>
            <input
              type="text"
              value={transferSettings.notes}
              onChange={(e) =>
                setTransferSettings((prev) => ({
                  ...prev,
                  notes: e.target.value,
                }))
              }
              placeholder="Additional notes..."
              className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-rose-500 dark:bg-slate-700 dark:text-white text-sm"
            />
          </div>
        </div>
      </div>

      {/* Items Table */}
      {rows.length > 0 && (
        <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-gray-200 dark:border-slate-700">
          <div className="overflow-x-auto overflow-y-visible relative">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-slate-700">
              <thead className="bg-gray-50 dark:bg-slate-700">
                <tr>
                  <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    SNo
                  </th>
                  <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Prod Id
                  </th>
                  <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Prod Name
                  </th>
                  <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    UOM
                  </th>
                  <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Stock Qty
                  </th>
                  <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Transfer Qty
                  </th>
                  <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Transfer Date
                  </th>
                  <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-slate-800 divide-y divide-gray-200 dark:divide-slate-700">
                {rows.map((row, idx) => (
                  <tr
                    key={row.id}
                    className="hover:bg-gray-50 dark:hover:bg-slate-700"
                  >
                    <td className="px-3 sm:px-6 py-2 sm:py-4 whitespace-nowrap text-xs sm:text-sm text-gray-900 dark:text-white">
                      {idx + 1}
                    </td>
                    <td className="px-3 sm:px-6 py-2 sm:py-4 whitespace-nowrap text-xs sm:text-sm text-gray-900 dark:text-white">
                      {row.prodId}
                    </td>
                    <td className="px-3 sm:px-6 py-2 sm:py-4 whitespace-nowrap">
                      {editingRow === row.id ? (
                        <div className="relative">
                          <input
                            type="text"
                            value={row.prodName}
                            onChange={(e) => {
                              const value = e.target.value;
                              handleInputChange(row.id, "prodName", value);
                            }}
                            placeholder="Search product name..."
                            className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-rose-500 dark:bg-slate-700 dark:text-white text-sm"
                          />
                          {row.prodName && !row.prodId && (
                            <div className="absolute z-[9999] w-full bottom-full mb-1 bg-white dark:bg-slate-700 border border-gray-300 dark:border-slate-600 rounded-lg shadow-2xl max-h-60 overflow-y-auto">
                              {getAllItems()
                                .filter((item) =>
                                  item.prodName
                                    .toLowerCase()
                                    .includes(row.prodName.toLowerCase())
                                )
                                .slice(0, 10)
                                .map((item) => (
                                  <div
                                    key={item.prodId}
                                    className="px-3 py-2 hover:bg-gray-100 dark:hover:bg-slate-600 cursor-pointer text-sm border-b border-gray-100 dark:border-slate-600 last:border-b-0"
                                    onClick={() =>
                                      handleProductNameChange(
                                        row.id,
                                        item.prodName
                                      )
                                    }
                                  >
                                    <div className="font-medium text-gray-900 dark:text-white">
                                      {item.prodName}
                                    </div>
                                    <div className="text-xs text-gray-500 dark:text-gray-400">
                                      ID: {item.prodId} | Stock: {item.stockQty}{" "}
                                      {item.uom}
                                    </div>
                                  </div>
                                ))}
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="text-xs sm:text-sm font-medium text-gray-900 dark:text-white">
                          {row.prodName}
                        </div>
                      )}
                    </td>
                    <td className="px-3 sm:px-6 py-2 sm:py-4 whitespace-nowrap text-xs sm:text-sm text-gray-500 dark:text-gray-300">
                      {row.uom}
                    </td>
                    <td className="px-3 sm:px-6 py-2 sm:py-4 whitespace-nowrap text-xs sm:text-sm text-gray-500 dark:text-gray-300">
                      {row.stockQty}
                    </td>
                    <td className="px-3 sm:px-6 py-2 sm:py-4 whitespace-nowrap">
                      {editingRow === row.id ? (
                        <input
                          type="number"
                          value={row.transferQty}
                          onChange={(e) =>
                            handleInputChange(
                              row.id,
                              "transferQty",
                              e.target.value
                            )
                          }
                          className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-rose-500 dark:bg-slate-700 dark:text-white text-sm"
                        />
                      ) : (
                        <div className="text-xs sm:text-sm text-gray-900 dark:text-white">
                          {row.transferQty}
                        </div>
                      )}
                    </td>
                    <td className="px-3 sm:px-6 py-2 sm:py-4 whitespace-nowrap">
                      {editingRow === row.id ? (
                        <input
                          type="date"
                          value={row.transferDate}
                          onChange={(e) =>
                            handleInputChange(
                              row.id,
                              "transferDate",
                              e.target.value
                            )
                          }
                          className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-rose-500 dark:bg-slate-700 dark:text-white text-sm"
                        />
                      ) : (
                        <div className="text-xs sm:text-sm text-gray-500 dark:text-gray-300">
                          {row.transferDate}
                        </div>
                      )}
                    </td>
                    <td className="px-3 sm:px-6 py-2 sm:py-4 whitespace-nowrap text-xs sm:text-sm font-medium">
                      {editingRow === row.id ? (
                        <div className="flex gap-1 sm:gap-2">
                          <button
                            onClick={() => setEditingRow(null)}
                            className="text-green-600 hover:text-green-900 dark:text-green-400 dark:hover:text-green-300 p-1"
                          >
                            <FaCheck className="text-sm sm:text-base" />
                          </button>
                          <button
                            onClick={handleCancelEdit}
                            className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300 p-1"
                          >
                            <FaTimes className="text-sm sm:text-base" />
                          </button>
                        </div>
                      ) : (
                        <div className="flex gap-1 sm:gap-2">
                          <button
                            onClick={() => handleEditRow(row.id)}
                            className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300 text-xs sm:text-sm"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDeleteRow(row.id)}
                            className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300 text-xs sm:text-sm"
                          >
                            Delete
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Save Button */}
          <div className="p-3 sm:p-4 lg:p-6 border-t border-gray-200 dark:border-slate-700">
            <div className="flex justify-end">
              <button
                onClick={handleSaveAll}
                className="bg-green-500 hover:bg-green-600 text-white px-4 sm:px-6 py-2 rounded-lg flex items-center gap-1 sm:gap-2 transition-colors text-sm"
              >
                <FaCheck />
                <span className="hidden sm:inline">Save All Items</span>
                <span className="sm:hidden">Save All</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Error/Success Messages */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 px-3 sm:px-4 py-3 rounded-lg text-sm">
          {error}
        </div>
      )}
      {success && (
        <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-green-700 dark:text-green-400 px-3 sm:px-4 py-3 rounded-lg text-sm">
          {success}
        </div>
      )}

      {/* Toast Notification */}
      <AnimatePresence>
        {toast.show && (
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 50 }}
            className={`fixed bottom-4 right-4 px-4 sm:px-6 py-3 rounded-lg text-white text-sm ${
              toast.type === "error" ? "bg-red-500" : "bg-green-500"
            }`}
          >
            {toast.message}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
